import { I18n } from 'react-redux-i18n';
import cx from 'classnames';
import debounce from 'lodash/debounce';

import ActionsButton, { TRIGGER_TYPE } from '@common/components/ActionsButton';
import { MessageBox } from '@common/components/MessageBox';
import { getTestAttributes } from '@common/utils/tests';
import { getReviewedStatusState, updateReviewedStatusActions } from '@common/actions/invites';
import { getEmailTemplates } from '@common/actions/email-templates';
import { updateInviteStatus } from '@common/actions/invites';
import { getPedigreeFolders } from '@common/actions/pedigrees';
import { InviteStatus } from '@common/dto/invite-status';
import { NewTableColumnPredefinedActionType, NewTable } from '@common/components/new-table';
import { getModuleFolders } from '@common/actions/folders';
import { getModuleFolderFields } from '@common/actions/formats';
import { ViewInviteHistory } from '@common/components/view-invite-history';
import { AdminSmartListFunctionType } from '@common/dto/admin-function/type';
import { UpdateStatusConfig } from '@common/components/update-status-config';

import { MODULE_NAME } from '../../individuals';
import { sendInvite } from '../../actions/individuals';
import { ROOT_SYSTEM_FIELDS_ID } from '../../constants/folders';
import { CONTEXT_TYPE } from '../../constants/context';

import './invites-table.styles.scss';

export class InvitesTable extends React.Component {
  state = {
    adminSmartListFunctions: {},
    updateStatusProps: {
      opened: false,
      fhq4: false,
      id: null
    },
    unlockInviteProps: {
      header: I18n.t('Confirmation Message'),
      text: '',
      show: false,
      confirmText: I18n.t('Yes'),
      cancelText: I18n.t('No'),
      size: 'small',
    },
    viewInviteHistoryProps: {
      opened: false,
    },
  };

  get columns() {
    const defaultWidth = 150;

    return [
      { 
        disableActions: true,
        enableResizing: false,
        enableSorting: false,
        header: 'Actions',
        accessorKey: 'ACTIONS_COLUMNS_LOCAL',
        id: 'ACTIONS_COLUMNS_LOCAL',
        cell: ({ row: { original } }) => (
          <ActionsButton 
            id={original.id} 
            data={original} 
            menuItems={this.getActions(original)}
            setCurrent={this.handleSetCurrentSelectedRow}
          />
        ),
        minSize: 65,
        size: 65,
        className: 'table-actions-cell'
      },
      ...this.props.columns.map((x) => ({
        id: this.props.getColumnId(x),
        accessorKey: x.field.id.toString(),
        enableDragAndDrop: true,
        enableSorting: true,
        minSize: x.size || defaultWidth,
        size: x.size || defaultWidth,
        header: x.field.name,
        filter: {
          type: x.field.type,
          options: x.field.options,
          outputDateFormat: 'YYYY-MM-DD'
        }
      }))
    ]
  }

  componentDidMount() {
    this.props.getAdminSmartListFunctions().then((adminSmartListFunctions) => {
      this.setState((x) => ({ ...x, adminSmartListFunctions }));
    });
  }

  handleSetCurrentSelectedRow = (_, invite) => {
    this.props.onSelectedInvitesChange([invite]);
  }

  getTrProps = (state, { index }) => getTestAttributes(MODULE_NAME, 'invite', 'table-row', index);

  getTheadThProps = (tableData, rowData, { id }) => getTestAttributes(MODULE_NAME, 'invite', 'table-header-cell', id);

  getTdProps = (tableData, { index }, { id }) => getTestAttributes(MODULE_NAME, 'invite', 'table-cell', `${index}-${id}`);

  handleColumnResize = (columns) => {
    return this.props.onResize(columns);
  }

  handleColumnsOrderChange = (columnsId) => {
    const columns = []

    columnsId.forEach((id) => {
      const column = this.props.columns.find((x) => this.props.getColumnId(x) === id);

      if (column) {
        columns.push(column);
      }
    });

    return this.props.onColumnsChange(columns);
  }

  handleNewInviteOpen = (inviteId) => {
    this.props.onNewInviteOpen(inviteId);
  }

  handleQuestionnaireOpen = () => {
    this.props.onQuestionnaireOpen({
      name: I18n.t('patient'),
    });
  }

  handleReminderOpen = (x, item) => {
    if (item.locked === 'true') {
      this.props.showErrorMessage(I18n.t('inviteIsLockedToSendReminder'));

      return;
    }

    this.props.onReminderOpen({
      ...item,
      id: x,
    });
  }

  handleOpenUpdateReviewedStatusModal = async (item) => {
    try {
      const data = await getReviewedStatusState(item.id, item.fhq4);

      if (data.moveToFolder || data.sendEmail || data.transmitPedigreeToEMR) {
        this.setState((x) => ({
          ...x,
          updateStatusProps: {
            opened: true,
            id: item.id,
            fhq4: item.fhq4,
          }
        }));
      } else {
        this.updateInviteStatus(item, InviteStatus.reviewed);
      }

    } catch (err) {
      console.error(err);
      this.props.showErrorMessage(err?.entity || I18n.t('Failed to get status data'));
    }
  }

  closeUpdateReviewedStatusModal = () => {
    this.setState((x) => ({
      ...x,
      updateStatusProps: {
        opened: false,
      }
    }));
  }

  openViewInviteHistoryModal = (invite) => {
    this.setState((x) => ({
      ...x,
      viewInviteHistoryProps: {
        opened: true,
        invite
      }
    }));
  }

  closeViewInviteHistoryModal = () => {
    this.setState((x) => ({
      ...x,
      viewInviteHistoryProps: {
        opened: false,
      }
    }));
  }

  getUpdateStatusOptions(item) {
    return [
      { label: 'Completed Manually', onClick: () => this.updateInviteStatus(item, InviteStatus.completeManually) },
      { label: 'Inactive', onClick: () => this.updateInviteStatus(item, InviteStatus.inactive) },
      { label: 'Reviewed', onClick: () => this.handleOpenUpdateReviewedStatusModal(item) }
    ];
  }

  getActions(item) {
    const singlet = this.props.selectedIndividual?.singlet === "true";

    return [
      { label: 'New Invite', onClick: this.handleNewInviteOpen, },
      { label: 'Open Questionnaire', onClick: this.handleQuestionnaireOpen, },
      { label: 'Send Reminder', onClick: this.handleReminderOpen, },
      {
        label: (
          <ActionsButton
            className="InvitesTable__update-status__btn"
            withBackdrop={false}
            classes={{
              menuContainer: 'InvitesTable__update-status__menu'
            }}
            id={getTestAttributes(MODULE_NAME, 'update-status', 'dropdown').id}
            triggerType={TRIGGER_TYPE.hover}
            trigger={<React.Fragment>{I18n.t('Update Status')} <span style={{ marginLeft: 8 }} className="fa fa-angle-right" /></React.Fragment>}
          >
            {this.getUpdateStatusOptions(item).map((x) => (
              <div key={x.label} className="InvitesTable__update-status__menu__item" onClick={x.onClick}>{x.label}</div>
            ))}
          </ActionsButton>
        ),
      },
      { label: item.locked === "true" ? 'Unlock Invite' : 'Lock Invite', onClick: this.handleLockAndUnlockInvite },
      { label: 'View Invite History', onClick: this.handleViewInviteHistory, },
      { label: 'Open Datasheet', onClick: this.props.onDatasheetOpen, },
      { label: 'Open Pedigree', onClick: this.props.onPedigreeOpen, condition: singlet },
      { label: 'Generate Letter', onClick: this.props.onGenerateLetter, },
    ].filter(({ condition = true }) => condition).map((x) => ({ ...x, label: typeof x.label === 'string' ? I18n.t(x.label) : x.label }));
  }

  updateLockAndUnlockInvite = async (invite) => {
    try {
      await this.props.lockInvite({
        inviteId: invite.id,
        lock: invite.lock,
        fhq4: invite.fhq4,
      });
      this.closeUnlockInviteModal();
      this.props.getData(this.props.loadMetadata);
    } catch(err) {
      console.error(err);

      const errorMessage = err?.entity || I18n.t('Failed to save lock/unlock invite');
      this.props.showErrorMessage(errorMessage);

    }
  }

  closeUnlockInviteModal = () => {
    this.setState((x) => ({
      ...x,
      unlockInviteProps: {
        ...x.unlockInviteProps,
        show: false,
      },
    }));
  };

  handleLockAndUnlockInvite = (_, invite) => {

    const { id, fhq4, locked } = invite;

    const lock = locked !== "true";

    const lockMessage = `${I18n.t('After locking this invite the patient will not be able to edit the questionnaire')}. ${I18n.t('Are you sure you want to lock this invite? You can unlock it again later if needed')}`;
    const unlockMessage = `${I18n.t('If you unlock this invite the patient will be able to access the questionnaire and modify data')}. ${I18n.t('Are you sure you want to unlock?')}`;

    const message = lock ? lockMessage : unlockMessage;

    this.setState((x) => ({
      ...x,
      unlockInviteProps: {
        ...x.unlockInviteProps,
        show: true,
        text: message,
        hideMessageBox: this.closeUnlockInviteModal,
        onCustomOk: () => this.updateLockAndUnlockInvite({
          id,
          fhq4,
          lock,
        }),
      }

    }))
  }

  handleViewInviteHistory = (_, invite) => {
    this.openViewInviteHistoryModal(invite);
  }

  handleSelectedItemsChange = (allSelectedItems) => {
    const { onSelectedInvitesChange } = this.props;

    onSelectedInvitesChange(allSelectedItems);
  }

  get columnActions() {
    return [
      { type: NewTableColumnPredefinedActionType.filters },
      { feature: AdminSmartListFunctionType.restoreColumnsToDefault, label: I18n.t('Restore Columns to Default'), onClick: this.props.onRestoreColumnsToDefault },
      { feature: AdminSmartListFunctionType.addColumns, type: NewTableColumnPredefinedActionType.addColumn },
      { feature: AdminSmartListFunctionType.deleteColumn, type: NewTableColumnPredefinedActionType.deleteColumn },
      { type: NewTableColumnPredefinedActionType.clearSorting },
    ].filter(({ feature }) => feature ? this.state.adminSmartListFunctions[feature] : true).map(({ feature, ...otherProps }) => otherProps);;
  }

  get headerActions() {
    return [
      { feature: AdminSmartListFunctionType.restoreColumnsToDefault, label: I18n.t('Restore Columns to Default'), onClick: this.props.onRestoreColumnsToDefault },
      { feature: AdminSmartListFunctionType.addColumns, type: NewTableColumnPredefinedActionType.addColumn },
    ].filter(({ feature }) => feature ? this.state.adminSmartListFunctions[feature] : true).map(({ feature, ...otherProps }) => otherProps);;
  }

  handleColumnAdd = ({ field, folder, columnId }) => {
    const columnIndex = this.columns.findIndex((x) => x.id === columnId);
    const isSystemRootFolder = ROOT_SYSTEM_FIELDS_ID === folder?.id;

    const column = {
      width: 100,
      field: {
        id: field.fieldId,
        name: field.fieldName,
        context: (isSystemRootFolder ? CONTEXT_TYPE.invites : CONTEXT_TYPE.individual) ?? field.fieldContext,
        type: field.fieldTypeId,
        system: field.systemField
      },
      sortOrder: 0,
      sortAsc: undefined

    };
    const columnsCopy = [...this.props.columns];
    
    columnsCopy.splice(columnIndex, 0, column);
    
    return this.props.onColumnAdd(columnsCopy);
  }

  handleColumnRemove = (columnId) => {
    const columnIndex = this.columns.findIndex((x) => x.id === columnId);
    const columnsCopy = [...this.props.columns];
    const actionsColumn = 1;

    columnsCopy.splice(columnIndex - actionsColumn, 1);

    return this.props.onColumnRemove(columnsCopy);
  }

  handleGetFields = ({ searchKeyword, folderId, system }) => {
    const noFolderSelected = 0;
    const isSystemRootFolder = ROOT_SYSTEM_FIELDS_ID === folderId;

    return getModuleFolderFields({
      searchQuery: searchKeyword,
      folderId: folderId || noFolderSelected,
      system,
      contextId: isSystemRootFolder ? CONTEXT_TYPE.invites : CONTEXT_TYPE.individual
    });
  }

  updateInviteStatus = async (item, status) => {
    try {
      await updateInviteStatus({
        inviteIds: [
          { id: item.id, fhq4: JSON.parse(item.fhq4) },
        ],
        status
      });
      this.props.getData(this.props.loadMetadata);
    } catch (err) {
      console.error(err);
      this.props.showErrorMessage(err?.entity || I18n.t('Failed to update status'));
    }
  }

  handleUpdateReviewedStatusSubmit = async ({ inviteIds, ...restData }) => {
    const processedInviteIds = inviteIds.map((x) => ({ ...x, fhq4: JSON.parse(x.fhq4) }));
    await updateInviteStatus({
      inviteIds: processedInviteIds,
      status: InviteStatus.reviewed,
    });
    await updateReviewedStatusActions({
      inviteIds: processedInviteIds,
      ...restData,
    });
    this.props.getData(this.props.loadMetadata);
    this.closeUpdateReviewedStatusModal();
  }

  getData = (params) => {
    const { filtering, sorting, pagination } = params;

    const processedSorting = sorting
      ? {
        ...sorting,
        index: this.columns.findIndex((x) => x.id === sorting.id),
      }
      : undefined

    return this.props.getData({
      filtering: filtering,
      pagination,
      sorting: processedSorting,
    });
  }

  getHeaderElementId = () => {
    return 'ind-home-tab-bpane-inv-tab-list-grid-thead-row0';
  }

  getHeaderCellElementId = (column, columnIndex) => {
    return `ind-home-tab-bpane-inv-tab-list-grid-thead-row0-cell${columnIndex}`;
  }

  getBodyRowElementId = (dataItem, rowIndex) => {
    return `ind-home-tab-bpane-inv-tab-list-grid-tbody-row${rowIndex}`;
  }

  getBodyCellElementId = (column, columnIndex, dataItem, rowIndex) => {
    return `ind-home-tab-bpane-inv-tab-list-grid-tbody-row${rowIndex}-cell${columnIndex}`;
  }

  getModuleFoldersForColumns = () => {
    const individualsId = 1;

    return getModuleFolders({ folderTypeId: individualsId, setUp: { rootFolderImgUrl: 'gif/df_individual.gif', rootFolderName: I18n.t('Individual Data Folders') } });
  }

  render() {
    const { className, filters, totalCount, getData, data, loadMetadata, setLoadMetadata, individualSelected, selectedInvites, showErrorMessage } = this.props;
    const { updateStatusProps, unlockInviteProps, viewInviteHistoryProps } = this.state;

    return (
      <div className={cx('InvitesTable', className)}>
        {updateStatusProps.opened && (
          <UpdateStatusConfig
            id={updateStatusProps.id}
            fhq4={updateStatusProps.fhq4}
            getState={getReviewedStatusState}
            getFolders={getPedigreeFolders}
            getEmailTemplates={getEmailTemplates}
            showErrorMessage={showErrorMessage}
            close={this.closeUpdateReviewedStatusModal}
            onSubmit={this.handleUpdateReviewedStatusSubmit}
          />
        )}
        <MessageBox {...unlockInviteProps} />
        {viewInviteHistoryProps.opened && (
          <ViewInviteHistory
            onClose={this.closeViewInviteHistoryModal}
            individual={this.props.selectedIndividual}
            invite={viewInviteHistoryProps.invite}
            showErrorMessage={showErrorMessage}
            getInviteHistory={this.props.getInviteHistory}
          />
        )}
        <NewTable
          disableAutoInit
          infiniteScroll
          noDataMessage={individualSelected ? I18n.t('There are no invites to display') : I18n.t('Select an individual to view invites')}
          errorMessage={{ list: I18n.t('Failed to load invites') }}
          moduleName={`${MODULE_NAME}_invites_table`}
          columnsOrder={{
            onChange: this.handleColumnsOrderChange,
          }}
          columnActions={this.columnActions}
          headerActions={this.headerActions}
          columnsResizing={{
            onChange: this.handleColumnResize,
          }}
          getId={{
            header: this.getHeaderElementId,
            headerCell: this.getHeaderCellElementId,
            bodyRow: this.getBodyRowElementId,
            bodyCell: this.getBodyCellElementId,
          }}
          selection={{
            type: 'single',
            items: selectedInvites,
            onChange: this.handleSelectedItemsChange
          }}
          filters={{
            operators: filters,
          }}
          itemUniqProperty={this.props.customInviteUniqProperty}
          data={data}
          columns={this.columns}
          totalItemsCount={totalCount}
          loadMetadata={loadMetadata}
          columnsManagement={{
            onAdd: this.handleColumnAdd,
            onRemove: this.handleColumnRemove,
            getFolders: this.getModuleFoldersForColumns,
            getFields: this.handleGetFields,
          }}
          onLoadData={this.getData}
        />
      </div>
    );
  }
}